import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCoffees } from "../hooks/useCoffees";
import { api } from "../lib/api";
import { brewParamChips } from "../lib/brew";
import { canonicalVariety, varietyKey } from "../lib/variety";
import WorldMap from "../components/WorldMap";
import { isTea } from "../types";
import type { BrewFeedEntry } from "../types";

const dayKey = (loggedAt: string) => new Date(loggedAt + "Z").toLocaleDateString("en-CA"); // YYYY-MM-DD, local
const monthKey = (loggedAt: string) => dayKey(loggedAt).slice(0, 7); // YYYY-MM
const avg = (ns: number[]) => (ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : 0);

export function Stats() {
  const { data: rawCoffees } = useCoffees();
  const [rawFeed, setRawFeed] = useState<BrewFeedEntry[] | null>(null);
  useEffect(() => { api.brews().then(setRawFeed).catch(() => setRawFeed([])); }, []);

  // This page is about COFFEE only — tea (matcha/gyokuro/sencha/hojicha) is
  // excluded from every stat. Null is preserved so the loading state still works.
  const coffees = useMemo(() => (rawCoffees ? rawCoffees.filter((c) => !isTea(c.kind)) : rawCoffees), [rawCoffees]);
  const feed = useMemo(() => (rawFeed ? rawFeed.filter((e) => !isTea(e.kind)) : rawFeed), [rawFeed]);

  // ── Collection passport (from the active inventory) ──────────────────────────
  const passport = useMemo(() => {
    const active = (coffees || []).filter((c) => !c.archived);
    const distinct = (fn: (c: typeof active[number]) => string | null | undefined) =>
      new Set(active.map(fn).filter((v): v is string => !!v && v.trim() !== "")).size;
    return {
      countries: distinct((c) => c.country),
      roasters: distinct((c) => c.roaster),
      varieties: distinct((c) => (c.variety ? varietyKey(c.variety) : null)),
      processes: distinct((c) => (isTea(c.kind) ? null : c.process)),
    };
  }, [coffees]);

  const scored = useMemo(() => (feed || []).filter((e) => e.score != null), [feed]);

  // ── Tasting / ratings ────────────────────────────────────────────────────────
  const tasting = useMemo(() => {
    if (scored.length === 0) return null;
    const scores = scored.map((e) => e.score as number);
    // best score per coffee → top rated
    const bestByCoffee = new Map<number, BrewFeedEntry>();
    for (const e of scored) {
      const cur = bestByCoffee.get(e.coffee_id);
      if (!cur || (e.score as number) > (cur.score as number)) bestByCoffee.set(e.coffee_id, e);
    }
    const topRated = [...bestByCoffee.values()].sort((a, b) => (b.score as number) - (a.score as number)).slice(0, 8);

    // score distribution by 5-point bucket
    const buckets = new Map<string, number>();
    for (const s of scores) {
      const lo = Math.floor(s / 5) * 5;
      const key = `${lo}–${lo + 4}`;
      buckets.set(key, (buckets.get(key) || 0) + 1);
    }
    const distribution = [...buckets.entries()].sort((a, b) => parseInt(b[0]) - parseInt(a[0]));

    const bestBy = (fn: (e: BrewFeedEntry) => string) => {
      const groups = new Map<string, number[]>();
      for (const e of scored) {
        const k = fn(e);
        if (!k) continue;
        (groups.get(k) || groups.set(k, []).get(k)!).push(e.score as number);
      }
      return [...groups.entries()].map(([k, v]) => ({ k, avg: avg(v), n: v.length }))
        .sort((a, b) => b.avg - a.avg);
    };

    return {
      average: avg(scores),
      count: scored.length,
      topRated,
      distribution,
      byCountry: bestBy((e) => e.country),
      byRoaster: bestBy((e) => e.roaster),
    };
  }, [scored]);

  // ── Recipe intelligence by characteristic (process / variety) ────────────────
  const recipeIntel = useMemo(() => {
    const group = (fn: (e: BrewFeedEntry) => string) => {
      const groups = new Map<string, BrewFeedEntry[]>();
      for (const e of scored) {
        const k = fn(e);
        if (!k) continue;
        (groups.get(k) || groups.set(k, []).get(k)!).push(e);
      }
      return [...groups.entries()].map(([k, entries]) => {
        const best = entries.reduce((a, b) => ((b.score as number) > (a.score as number) ? b : a));
        return { k, avg: avg(entries.map((e) => e.score as number)), n: entries.length, best };
      }).sort((a, b) => b.avg - a.avg);
    };
    return { byProcess: group((e) => e.process), byVariety: group((e) => (e.variety ? canonicalVariety(e.variety) : "")) };
  }, [scored]);

  // ── Learning journey ─────────────────────────────────────────────────────────
  const journey = useMemo(() => {
    if (!feed) return null;
    // brews per month + monthly average score (learning curve)
    const perMonth = new Map<string, { count: number; scores: number[] }>();
    for (const e of feed) {
      const m = monthKey(e.logged_at);
      const cur = perMonth.get(m) || { count: 0, scores: [] };
      cur.count += 1;
      if (e.score != null) cur.scores.push(e.score);
      perMonth.set(m, cur);
    }
    const months = [...perMonth.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([m, v]) => ({ month: m, count: v.count, avg: avg(v.scores), hasScore: v.scores.length > 0 }));

    // streaks over distinct brew days
    const days = [...new Set(feed.map((e) => dayKey(e.logged_at)))].sort();
    let longest = 0, current = 0, run = 0;
    for (let i = 0; i < days.length; i++) {
      if (i > 0 && (Date.parse(days[i]) - Date.parse(days[i - 1])) === 86400000) run += 1;
      else run = 1;
      longest = Math.max(longest, run);
      current = run; // run ending at the latest brew day
    }

    // most dialed-in coffees (most brews)
    const byCoffee = new Map<number, { e: BrewFeedEntry; count: number; scores: { s: number }[] }>();
    for (const e of feed) {
      const cur = byCoffee.get(e.coffee_id) || { e, count: 0, scores: [] };
      cur.count += 1;
      if (e.score != null) cur.scores.push({ s: e.score });
      byCoffee.set(e.coffee_id, cur);
    }
    const mostBrewed = [...byCoffee.values()].sort((a, b) => b.count - a.count).slice(0, 6);

    // biggest improvement: best − first among coffees with ≥2 scored brews (feed is chronological)
    const improvements = [...byCoffee.values()]
      .filter((c) => c.scores.length >= 2)
      .map((c) => {
        const first = c.scores[0].s;
        const best = Math.max(...c.scores.map((x) => x.s));
        return { e: c.e, delta: best - first, first, best };
      })
      .filter((x) => x.delta > 0)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 6);

    return { total: feed.length, months, longest, current, mostBrewed, improvements };
  }, [feed]);

  const loading = !coffees || !feed;

  return (
    <div className="space-y-14">
      <header className="space-y-2">
        <div className="eyebrow">The Vault · Stats</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">Your coffee journey</h1>
        <p className="text-sm text-paper-600 max-w-xl">
          What you've explored, how your cups score, and whether your brewing is improving over time.
        </p>
      </header>

      {/* Passport */}
      <section className="space-y-4">
        <div className="eyebrow">Collection passport</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-paper-200 border border-paper-200">
          <Stat label="Countries" value={passport.countries} />
          <Stat label="Roasters" value={passport.roasters} />
          <Stat label="Varieties" value={passport.varieties} />
          <Stat label="Processes" value={passport.processes} />
        </div>
      </section>

      {/* World map — where your coffee has taken you */}
      <section className="space-y-4">
        <div className="eyebrow">Where your coffee's from</div>
        <WorldMap coffees={(coffees || []).filter((c) => !c.archived)} />
      </section>

      {loading ? <p className="text-paper-500">Loading…</p> : null}

      {/* Learning journey */}
      {journey && journey.total > 0 ? (
        <section className="space-y-4">
          <div className="eyebrow">Learning journey</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-paper-200 border border-paper-200">
            <Stat label="Brews logged" value={journey.total} />
            <Stat label="Current streak" value={journey.current} sub="days in a row" />
            <Stat label="Longest streak" value={journey.longest} sub="days" />
            <Stat label="Avg score" value={tasting ? Number(tasting.average.toFixed(1)) : 0} />
          </div>

          {journey.months.some((m) => m.hasScore) ? (
            <Panel title="Learning curve — average score by month">
              {journey.months.filter((m) => m.hasScore).map((m) => (
                <BarRow key={m.month} label={m.month} value={m.avg} display={m.avg.toFixed(1)} max={100} />
              ))}
            </Panel>
          ) : null}

          <Panel title="Brews per month">
            {journey.months.map((m) => (
              <BarRow key={m.month} label={m.month} value={m.count} display={String(m.count)}
                max={Math.max(...journey.months.map((x) => x.count))} />
            ))}
          </Panel>

          {journey.improvements.length ? (
            <Panel title="Biggest improvement (best − first score)">
              {journey.improvements.map((x) => (
                <div key={x.e.coffee_id} className="flex justify-between py-1.5 border-b border-paper-200 text-sm">
                  <Link to={`/coffee/${x.e.coffee_id}`} className="hover:underline">{x.e.variety} · {x.e.roaster}</Link>
                  <span className="tabular-nums text-paper-600">{x.first} → {x.best} <span className="text-emerald-700">+{x.delta.toFixed(1)}</span></span>
                </div>
              ))}
            </Panel>
          ) : null}

          {journey.mostBrewed.length ? (
            <Panel title="Most dialed-in (brew count)">
              {journey.mostBrewed.map((c) => (
                <BarRow key={c.e.coffee_id} label={`${c.e.variety} · ${c.e.roaster}`} to={`/coffee/${c.e.coffee_id}`}
                  value={c.count} display={String(c.count)} max={journey.mostBrewed[0].count} />
              ))}
            </Panel>
          ) : null}
        </section>
      ) : !loading ? (
        <p className="text-sm text-paper-500">No brews logged yet — log a brew from any coffee's page to start your journey.</p>
      ) : null}

      {/* Tasting */}
      {tasting ? (
        <section className="space-y-4">
          <div className="eyebrow">Tasting · {tasting.count} scored brews</div>

          <Panel title="Top rated">
            {tasting.topRated.map((e) => (
              <div key={e.coffee_id} className="flex justify-between py-1.5 border-b border-paper-200 text-sm">
                <Link to={`/coffee/${e.coffee_id}`} className="hover:underline">{e.variety} · {e.roaster}</Link>
                <span className="tabular-nums font-medium">{e.score}</span>
              </div>
            ))}
          </Panel>

          <Panel title="Score distribution">
            {tasting.distribution.map(([bucket, n]) => (
              <BarRow key={bucket} label={bucket} value={n} display={String(n)}
                max={Math.max(...tasting.distribution.map(([, x]) => x))} />
            ))}
          </Panel>

          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="Best countries (avg score)">
              {tasting.byCountry.slice(0, 6).map((g) => (
                <BarRow key={g.k} label={`${g.k} (${g.n})`} value={g.avg} display={g.avg.toFixed(1)} max={100} />
              ))}
            </Panel>
            <Panel title="Best roasters (avg score)">
              {tasting.byRoaster.slice(0, 6).map((g) => (
                <BarRow key={g.k} label={`${g.k} (${g.n})`} value={g.avg} display={g.avg.toFixed(1)} max={100} />
              ))}
            </Panel>
          </div>
        </section>
      ) : null}

      {/* Recipe intelligence */}
      {tasting ? (
        <section className="space-y-4">
          <div className="eyebrow">Recipe intelligence — what works for you</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="By process">
              {recipeIntel.byProcess.map((g) => (
                <CharRow key={g.k} name={g.k} avg={g.avg} n={g.n} recipe={brewParamChips(g.best).join(" · ")} />
              ))}
            </Panel>
            <Panel title="By variety / cultivar">
              {recipeIntel.byVariety.slice(0, 10).map((g) => (
                <CharRow key={g.k} name={g.k} avg={g.avg} n={g.n} recipe={brewParamChips(g.best).join(" · ")} />
              ))}
            </Panel>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="bg-paper-50 px-6 py-8 flex flex-col gap-2">
      <span className="eyebrow">{label}</span>
      <span className="font-display text-4xl tabular-nums">{value}</span>
      {sub ? <span className="text-xs text-paper-500">{sub}</span> : null}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card px-5 py-4">
      <div className="eyebrow mb-3">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function BarRow({ label, value, display, max, to }: { label: string; value: number; display: string; max: number; to?: string }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const name = to ? <Link to={to} className="hover:underline">{label}</Link> : label;
  return (
    <div className="flex items-center gap-3 py-1 text-sm">
      <div className="w-40 shrink-0 truncate text-paper-700">{name}</div>
      <div className="flex-1 bg-paper-100 h-3">
        <div className="h-3 bg-ink" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 text-right tabular-nums text-paper-600">{display}</div>
    </div>
  );
}

function CharRow({ name, avg, n, recipe }: { name: string; avg: number; n: number; recipe: string }) {
  return (
    <div className="py-2 border-b border-paper-200">
      <div className="flex justify-between text-sm">
        <span className="text-paper-800">{name} <span className="text-paper-400">({n})</span></span>
        <span className="tabular-nums font-medium">{avg.toFixed(1)}</span>
      </div>
      {recipe ? <div className="text-xs text-paper-500 mt-0.5">{recipe}</div> : null}
    </div>
  );
}
